import { Gift, Globe, Home, Search, Settings, ShoppingBag, Wallet } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/shadcn/lib/utils";

type PatientAppShellProps = {
  readonly variant: "home" | "shop" | "discover";
  readonly children: ReactNode;
  readonly tabs?: string[];
  readonly activeTab?: string;
  readonly topContent?: ReactNode;
};

const NAV_ITEMS = [
  { label: "Home", Icon: Home },
  { label: "Discover", Icon: Globe },
  { label: "Shop", Icon: ShoppingBag },
  { label: "Rewards", Icon: Gift },
  { label: "Wallet", Icon: Wallet },
] as const;

const HeaderBar = ({ variant }: { variant: PatientAppShellProps["variant"] }) => {
  const title = variant === "home" ? "" : variant === "shop" ? "Shop" : "Discover";

  return (
    <div className="px-3 pt-2 pb-1">
      <div className="flex items-center justify-between">
        <span className="type-preview-md font-semibold">{title}</span>
        <div className="flex gap-1.5">
          <Search className="h-3 w-3 text-neutral-500" />
          <Settings className="h-3 w-3 text-neutral-500" />
        </div>
      </div>
    </div>
  );
};

const TabBar = ({ tabs, activeTab }: { tabs: string[]; activeTab?: string }) => (
  <div className="flex gap-2 overflow-x-auto border-b border-neutral-200 px-3">
    {tabs.map((tab) => (
      <span
        className={cn(
          "pb-1 type-preview-sm font-medium whitespace-nowrap",
          tab === activeTab ? "border-b-2 border-red-400 text-red-400" : "text-neutral-400",
        )}
        key={tab}
      >
        {tab}
      </span>
    ))}
  </div>
);

const BottomNavBar = () => (
  <div className="border-t border-neutral-200 bg-white px-2 pt-1.5 pb-1">
    <div className="flex justify-around">
      {NAV_ITEMS.map(({ label, Icon }) => (
        <div className="flex flex-col items-center gap-0.5" key={label}>
          <Icon className="h-3 w-3 text-neutral-400" />
          <span className="type-preview-xs text-neutral-400">{label}</span>
        </div>
      ))}
    </div>
  </div>
);

export const PatientAppShell = ({
  variant,
  children,
  tabs,
  activeTab,
  topContent,
}: PatientAppShellProps) => {
  return (
    <div className="flex h-[480px] flex-col bg-neutral-50">
      {topContent}
      <HeaderBar variant={variant} />
      {tabs && <TabBar activeTab={activeTab} tabs={tabs} />}
      <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      <BottomNavBar />
    </div>
  );
};
